import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {
    useAddWarrantyClaimsLaborMutation,
    useGetWarrantyClaimsLaborListQuery,
    useUpdateWarrantyClaimsLaborMutation,
    useDeleteWarrantyClaimsLaborMutation,
    useGetWarrantyClaimsByIdQuery,
    useCalculateWarrantyClaimTotalsQuery
} from '../../../../../../../api';
import { selectSiteUseId } from '../../../../../../../store/slices/userParamsSlice';
import { selectUserId, selectUserName, selectUserRoles, selectIsAuthenticated, selectCsrfToken, selectTokenExpiration, selectAftersalesInfo, selectIsAftersalesUser, selectAftersalesRoles } from '../../../../../../../features/auth/authSlice';
import LaborFormDialog from '../detailDialogForms/LaborFormDialog';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { Roles } from '../../../../../../../utils/roleEnums';
import toastService from '../../../../../../../utils/toastService';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';

const DISABLED_STATUSES = [WarrantyClaimStatus.RETURNED_TO_KARSAN, WarrantyClaimStatus.ACCEPTED, WarrantyClaimStatus.REJECTED];

const LaborCatalogTable = ({ claimId, authorizationCheck = false }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();

    const {
        data: apiWarrantyClaimsLaborList,
        isLoading,
        refetch
    } = useGetWarrantyClaimsLaborListQuery({
        WarrantyClaimsId: claimId,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });

    const { data: warrantyClaimGetById } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() });
    const { refetch: refetchTotals } = useCalculateWarrantyClaimTotalsQuery({ warrantyClaimId: claimId }, { skip: !claimId });
    //Örnek authSlice veri çekme
    const userId = useSelector(selectUserId);
    const userName = useSelector(selectUserName);
    const userRoles = useSelector(selectUserRoles);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const csrfToken = useSelector(selectCsrfToken);
    const tokenExpiration = useSelector(selectTokenExpiration);
    const aftersalesInfo = useSelector(selectAftersalesInfo);
    const isAftersalesUser = useSelector(selectIsAftersalesUser);
    const aftersalesRoles = useSelector(selectAftersalesRoles);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingPart, setEditingPart] = useState(null);
    const siteUseId = useSelector(selectSiteUseId);

    const [lazyParams, setLazyParams] = useState({
        rows: 10,
        sortField: null,
        sortOrder: null
    });

    const [addWarrantyClaimsLabor] = useAddWarrantyClaimsLaborMutation();
    const [updateWarrantyClaimsLabor] = useUpdateWarrantyClaimsLaborMutation();
    const [deleteWarrantyClaimsLabor] = useDeleteWarrantyClaimsLaborMutation();

    const disabledStatuses = DISABLED_STATUSES.includes(warrantyClaimGetById?.status);
    const disabledForRole = (hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole()) && warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING;

    useEffect(() => {
        if (claimId) {
            refetch();
        }
    }, [claimId, refetch]);

    const sortedData = useMemo(() => {
        const allItems = apiWarrantyClaimsLaborList?.data?.items || [];

        if (!lazyParams.sortField || lazyParams.sortOrder === 0) {
            return allItems;
        }

        return [...allItems].sort((a, b) => {
            let aValue = a[lazyParams.sortField];
            let bValue = b[lazyParams.sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return lazyParams.sortOrder;
            if (bValue == null) return -lazyParams.sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return lazyParams.sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -lazyParams.sortOrder;
            if (aValue > bValue) return lazyParams.sortOrder;
            return 0;
        });
    }, [apiWarrantyClaimsLaborList?.data?.items, lazyParams.sortField, lazyParams.sortOrder]);

    const handleNewPart = () => {
        setEditingPart(null);
        setDialogVisible(true);
    };

    const handleSave = async (data) => {
        try {
            let result;
            const dataWithSiteUseId = {
                ...data,
                siteUseId: siteUseId || 0
            };

            if (data?.id) {
                result = await updateWarrantyClaimsLabor(dataWithSiteUseId).unwrap();
            } else {
                result = await addWarrantyClaimsLabor(dataWithSiteUseId).unwrap();
            }

            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Parça kaydedilirken hata:', error);
        }
    };

    const handleEdit = (rowData) => {
        setEditingPart(rowData);
        setDialogVisible(true);
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleDelete = async (labor) => {
        try {
            const result = await deleteWarrantyClaimsLabor(labor.id).unwrap();
            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                toastService.success(th('warrantyClaims.detail.labor.deleteSuccess'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('İşçilik silinirken hata:', error);
            toastService.error(th('warrantyClaims.detail.labor.deleteError'));
            return false;
        }
    };
    const actionBodyTemplate = (rowData) => {
        const isPackageIdValid = rowData.packageId != null && rowData.packageId !== 0;
        const isDisabled = disabledStatuses || isPackageIdValid;

        return (
            <div className="flex gap-2 justify-content-center">
                <i
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleEdit(rowData)}
                    className={`pi pi-pencil ${isDisabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-blue-500 hover:text-blue-700'}`}
                    style={{ fontSize: '1.1rem' }}
                    title={th('warrantyClaims.detail.labor.edit')}
                ></i>
            </div>
        );
    };

    const isExternalLaborBodyTemplate = (rowData) => {
        return rowData.isExternalLabor ? th('warrantyClaims.detail.labor.externalLabor') : th('warrantyClaims.detail.labor.notExternalLabor');
    };

    return (
        <div>
            <div className="flex justify-content-start align-items-center mb-3 mt-3">
                <Button
                    label={th('warrantyClaims.detail.labor.addNew')}
                    icon="pi pi-plus"
                    onClick={handleNewPart}
                    disabled={disabledStatuses || warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING || warrantyClaimGetById?.warrType === 'G' || warrantyClaimGetById?.warrType === 'B'}
                />
            </div>
            <DataTable
                value={sortedData}
                className="p-datatable-gridlines"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="id"
                loading={isLoading}
                emptyMessage={th('warrantyClaims.detail.labor.emptyMessage')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column body={actionBodyTemplate} style={{ width: '5%', minWidth: '33px' }} bodyClassName="text-center" headerClassName="text-center" exportable={false} />
                <Column field="warrantyTypeName" header={th('warrantyClaims.detail.partDialog.warrantyType')} sortable />
                <Column field="isExternalLabor" header={th('warrantyClaims.detail.labor.isExternalLabor')} body={isExternalLaborBodyTemplate} sortable />
                <Column field="laborName" header={th('warrantyClaims.detail.labor.laborCatalog')} sortable />
                <Column field="description" header={th('warrantyClaims.detail.labor.description')} sortable />
                <Column field="laborTime" header={th('warrantyClaims.detail.labor.laborTime')} sortable />
                <Column field="quantity" header={th('warrantyClaims.detail.labor.quantity')} sortable />
                <Column field="dealerWarrHourlyRate" header={th('warrantyClaims.detail.labor.hourlyRate')} sortable />
                <Column field="laborPrice" header={th('warrantyClaims.detail.labor.totalAmount')} sortable />
                <Column sortable field="acceptedAmount" header={th('warrantyClaims.detail.partTable.acceptedAmount')} />
                <Column field="dealerCurrencyUnits" header={th('warrantyClaims.detail.labor.currency')} sortable />
            </DataTable>
            <LaborFormDialog
                disabledStatuses={disabledStatuses || disabledForRole}
                authorizationCheck={authorizationCheck}
                claimId={claimId}
                warrType={warrantyClaimGetById?.warrType}
                dialogVisible={dialogVisible}
                hideDialog={() => setDialogVisible(false)}
                onSave={handleSave}
                onDelete={handleDelete}
                editingPart={editingPart}
                packageId={editingPart?.packageId}
                onUpdateSuccess={() => {
                    // Update işleminden sonra liste verilerini yenile
                    refetch();
                }}
            />
        </div>
    );
};

export default LaborCatalogTable;
